// Ticks on the device as a row per tick (DD, 2026-10-22; docs/SCHEMA.md
// "Site requirements"): an area's access, hazard and preliminary ticks
// (areatag → hig_areatag) and a visit area's condition ticks (visitareatag →
// hig_visitareatag). Saved here first and synced like any record, so ticking
// works with no signal. Unticking makes the row inactive; it is never deleted.
//
// A row's id comes from its area's and the tick's name (the tag's key, or a
// typed entry's text — @higgins/m2q-pricing tickWrites), so two iPads ticking
// the same thing make one row, which CRM merges.
//
// Rows are read first; an area with none still reads the JSON column they
// replace (hig_sitereqs, hig_conditiontags). Only rows are written.
import { conditionRecordId } from '@higgins/condition-library';
import { parseSiteReqs, readSiteReqs, tickWrites, type AccessTag, type SiteReqsDoc, type TickRow, type TickSource } from '@higgins/m2q-pricing';
import { parseTicks } from './condition';
import { getRecord, listRecords, saveRecord, type DeviceRecord, type DeviceTable, type Fields } from './device-db';

export type TickTable = 'areatag' | 'visitareatag';

/**
 * A tick is one fact, ticked or not, so its row is always sent as a create,
 * which CRM applies over whatever is there (dvCreate): two iPads never
 * conflict over a tick — the later save wins (outbox.ts).
 */
export const isTickTable = (table: DeviceTable): table is TickTable => table === 'areatag' || table === 'visitareatag';

const ACTIVE = 'Active';
const INACTIVE = 'Inactive';

/** Where a tick's rows hang: the area (or visit area), and the job record every row also carries for the job download. */
export interface TickOwner {
  table: TickTable;
  parent: DeviceRecord;
  /** hig_measureid (an area's measure) or hig_sitevisitid (a visit area's visit). */
  jobLink: { column: 'hig_measureid' | 'hig_sitevisitid'; id: string };
}

/** The JSON column each table's rows replace, on the parent. */
const JSON_COLUMN: Record<TickTable, string> = { areatag: 'hig_sitereqs', visitareatag: 'hig_conditiontags' };

const textOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

export const tickRowsOf = (records: readonly DeviceRecord[], table: TickTable, parentId: string): DeviceRecord[] =>
  records.filter((r) => r.table === table && r.parentId === parentId);

export const asTickRow = (record: DeviceRecord): TickRow => ({
  tagId: textOrNull(record.fields.hig_accesstagid),
  label: String(record.fields.hig_name ?? ''),
  category: textOrNull(record.fields.hig_category),
  active: record.fields.statecode !== INACTIVE,
});

/** A parent's ticks as stored: its rows, and the JSON read when it has none. */
export const tickSource = (records: readonly DeviceRecord[], table: TickTable, parent: DeviceRecord | undefined): TickSource =>
  parent ? { json: textOrNull(parent.fields[JSON_COLUMN[table]]), rows: tickRowsOf(records, table, parent.id).map(asTickRow) } : { json: null, rows: [] };

/** The area's site requirements as the screens and pricing read them. Throws on JSON in a newer format. */
export const areaSiteReqs = (records: readonly DeviceRecord[], area: DeviceRecord, tags: readonly AccessTag[]): SiteReqsDoc =>
  readSiteReqs(tickSource(records, 'areatag', area), tags);

/** The area's measure, which its tick rows carry: area → building → measure. */
export function areaTickOwner(records: readonly DeviceRecord[], area: DeviceRecord): TickOwner | null {
  const building = records.find((r) => r.table === 'building' && r.id === area.parentId);
  return building ? { table: 'areatag', parent: area, jobLink: { column: 'hig_measureid', id: building.parentId } } : null;
}

/** A visit area's condition ticks (tag keys in .tags): its rows, else its hig_conditiontags. None before it has a visit area. */
export function conditionTicks(records: readonly DeviceRecord[], visitArea: DeviceRecord | undefined, tags: readonly AccessTag[]): SiteReqsDoc {
  const source = tickSource(records, 'visitareatag', visitArea);
  return source.rows.length ? readSiteReqs(source, tags) : { ...parseSiteReqs(null), tags: parseTicks(source.json) };
}

/** A visit area's ticks carry its visit. */
export const visitAreaTickOwner = (visitArea: DeviceRecord): TickOwner => ({ table: 'visitareatag', parent: visitArea, jobLink: { column: 'hig_sitevisitid', id: visitArea.parentId } });

/** A list the device cached before rows has no tag ids, so can't write a row; it updates when a job is opened with signal. */
export const canWriteTicks = (tags: readonly AccessTag[]): boolean => tags.every((t) => !!t.id);

function rowFields(owner: TickOwner, row: TickRow): Fields {
  return {
    hig_name: row.label,
    [owner.jobLink.column]: owner.jobLink.id,
    // A lookup is only ever set, never sent empty (records.ts), so a typed entry leaves it out.
    ...(row.tagId ? { hig_accesstagid: row.tagId } : {}),
    ...(owner.table === 'areatag' && row.category ? { hig_category: row.category } : {}),
    statecode: row.active ? ACTIVE : INACTIVE,
  };
}

const unchanged = (stored: DeviceRecord | undefined, row: TickRow) =>
  !!stored && stored.fields.statecode === (row.active ? ACTIVE : INACTIVE) && stored.fields.hig_name === row.label;

/**
 * Save a change of ticks from `before` to `after` (the documents the screen
 * showed and made) as rows. Whether the parent has rows yet, and each row,
 * are read fresh from the device, so two quick taps can't undo each other.
 */
export async function saveTicks(owner: TickOwner, before: SiteReqsDoc, after: SiteReqsDoc, tags: readonly AccessTag[]): Promise<void> {
  const hasRows = tickRowsOf(await listRecords(owner.parent.jobId), owner.table, owner.parent.id).length > 0;
  for (const write of tickWrites(before, after, tags, hasRows)) {
    const id = conditionRecordId(owner.table, owner.parent.id, write.name);
    const stored = await getRecord(id);
    if (unchanged(stored, write)) continue;
    await saveRecord({ id, table: owner.table, jobId: owner.parent.jobId, parentId: owner.parent.id, etag: stored?.etag ?? null, fields: rowFields(owner, write) });
  }
}

/** Untick one row as it is (a tag no longer in CRM's list, which no document holds). */
export async function untickRow(owner: TickOwner, row: DeviceRecord): Promise<void> {
  await saveRecord({ ...row, fields: rowFields(owner, { ...asTickRow(row), active: false }) });
}

/** A copied area's ticks: a new row for each of the original's that is ticked. */
export async function copyTicks(records: readonly DeviceRecord[], from: DeviceRecord, to: DeviceRecord, tags: readonly AccessTag[]): Promise<void> {
  const owner = areaTickOwner(records, to);
  if (!owner || !tickRowsOf(records, 'areatag', from.id).length) return;
  await saveTicks(owner, parseSiteReqs(null), areaSiteReqs(records, from, tags), tags);
}
